/**
 * Opens a new browser window with full property details + Google Maps.
 * The page is self-contained HTML (no React in child window).
 */

const MAPS_KEY = typeof import.meta !== 'undefined' ? (import.meta.env?.VITE_GOOGLE_MAPS_KEY || '') : '';

function formatBRL(v) {
  return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
function formatDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('pt-BR');
}

/** Escape a string for safe interpolation inside HTML text/attribute contexts. */
function escapeHtml(s) {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#x27;');
}

/** Coerce to finite number or return fallback — prevents coordinate injection. */
function safeNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** Validate a URL is http/https; returns the URL or '#'. */
function safeUrl(url) {
  try {
    const u = new URL(String(url ?? ''));
    return (u.protocol === 'https:' || u.protocol === 'http:') ? u.href : '#';
  } catch { return '#'; }
}

export function openPropertyWindow(property, isDark, enrichment) {
  const p = property;
  const bankColor = p.banco === 'Caixa' ? '#005CA9' : '#EC0000';

  const lat = safeNumber(p.lat);
  const lng = safeNumber(p.lng);
  const bankUrl = safeUrl(p.url);

  const lances = p.historicoLances ?? [];
  const lancesHTML = lances.length > 0
    ? `<div class="section">
        <h3>Lances</h3>
        <div class="lances-table">
          ${lances.slice(-6).map((l, i) => `
            <div class="lance-row" style="border-bottom:${i < Math.min(lances.length, 6) - 1 ? '1px solid var(--bd)' : 'none'}">
              <span class="lance-name">${escapeHtml(l.licitante)}</span>
              <span class="lance-value">${escapeHtml(formatBRL(l.valor))}</span>
            </div>
          `).join('')}
        </div>
      </div>` : '';

  // Parse area/quartos/banheiros from raw.descricao as fallback when DB fields are null
  const desc = p.raw?.descricao ?? '';
  const areaVal = p.area || (() => {
    const m = desc.match(/(\d[\d.,]*)\s*de\s*.rea\s*privativa/i)
           || desc.match(/(\d[\d.,]*)\s*de\s*.rea\s*total/i);
    if (m) { const v = parseFloat(m[1].replace(',','.')); return v > 0 ? Math.round(v) : null; }
    return null;
  })();
  const quartosVal = p.quartos || (() => {
    const m = desc.match(/(\d+)\s*qto/i) || desc.match(/(\d+)\s*quarto/i);
    return m ? parseInt(m[1]) : null;
  })();
  const banheiroVal = (() => {
    const m = desc.match(/(\d+)\s*(?:WC|banheiro|wc)/i);
    if (m) return parseInt(m[1]);
    return /\bWC\b/i.test(desc) ? 1 : null;
  })();
  const vagasVal = p.vagas || (() => {
    const m = desc.match(/(\d+)\s*vaga/i);
    return m ? parseInt(m[1]) : null;
  })();

  const specs = [
    areaVal   ? [areaVal + 'm\u00B2',       '\u00C1rea']    : null,
    quartosVal ? [quartosVal,                'Quartos']       : null,
    banheiroVal ? [banheiroVal,              'Banheiros']     : null,
    vagasVal  ? [vagasVal,                   'Vagas']         : null,
  ].filter(Boolean);

  // Image gallery — uses p.images[] populated by the enrichment worker
  const images = Array.isArray(p.images) ? p.images.filter(Boolean) : [];
  const galleryHTML = images.length > 0 ? `
    <div style="margin-bottom:20px;">
      <div id="gallery-main" style="width:100%;height:260px;border-radius:14px;overflow:hidden;background:url('${escapeHtml(images[0])}') center/cover no-repeat;background-color:var(--s2);"></div>
      ${images.length > 1 ? `
      <div style="display:flex;gap:6px;margin-top:8px;overflow-x:auto;padding-bottom:4px;">
        ${images.slice(0, 8).map((img, i) => `
          <div onclick="setGalleryImg(${i})" id="gthumb-${i}"
            style="width:70px;height:52px;flex-shrink:0;border-radius:7px;cursor:pointer;
                   background:url('${escapeHtml(img)}') center/cover no-repeat;
                   border:2px solid ${i === 0 ? 'var(--br)' : 'var(--bd)'};transition:border-color .15s;"></div>
        `).join('')}
      </div>` : ''}
    </div>` : '';

  const info = [
    ['Banco',      escapeHtml(p.banco)],
    ['Modalidade', escapeHtml(p.modalidade)],
    p.condicao  ? ['Pagamento', escapeHtml(p.condicao)]  : null,
    p.dataLeilao ? ['Data',      escapeHtml(formatDate(p.dataLeilao))] : null,
    p.matricula ? ['Matricula', 'n\u00BA ' + escapeHtml(p.matricula)] : null,
  ].filter(Boolean);

  // JS-safe strings for inline script (JSON.stringify produces valid JS string literals)
  const jsTitulo = JSON.stringify(String(p.titulo ?? ''));
  const jsInfoWindowContent = JSON.stringify('<strong>' + escapeHtml(p.titulo) + '</strong><br>' + escapeHtml(formatBRL(p.valorMinimo)));
  const jsDarkStyles = isDark
    ? '[{elementType:"geometry",stylers:[{color:"#242f3e"}]},{elementType:"labels.text.stroke",stylers:[{color:"#242f3e"}]},{elementType:"labels.text.fill",stylers:[{color:"#746855"}]}]'
    : '[]';

  const galleryScript = images.length > 1 ? `
    <script>
      var _galleryImgs = ${JSON.stringify(images)};
      function setGalleryImg(i) {
        document.getElementById('gallery-main').style.backgroundImage = 'url(' + _galleryImgs[i] + ')';
        document.querySelectorAll('[id^="gthumb-"]').forEach(function(el, j) {
          el.style.borderColor = j === i ? 'var(--br)' : 'var(--bd)';
        });
      }
    </script>` : '';

  const mapsScript = MAPS_KEY ? `
    <script>
      let map, marker, panorama;
      let currentView = 'map';
      function initMap() {
        const pos = { lat: ${lat}, lng: ${lng} };
        map = new google.maps.Map(document.getElementById('map'), {
          center: pos, zoom: 16,
          styles: ${jsDarkStyles}
        });
        marker = new google.maps.Marker({ position: pos, map, title: ${jsTitulo} });
        const iw = new google.maps.InfoWindow({ content: ${jsInfoWindowContent} });
        marker.addListener('click', () => iw.open(map, marker));
        panorama = new google.maps.StreetViewPanorama(document.getElementById('streetview'), {
          position: pos, pov: { heading: 34, pitch: 10 }
        });
      }
      function toggleView(v) {
        currentView = v;
        document.getElementById('map').style.display = v === 'map' ? 'block' : 'none';
        document.getElementById('streetview').style.display = v === 'streetview' ? 'block' : 'none';
        document.querySelectorAll('.map-toggle-btn').forEach(b => {
          b.classList.toggle('active', b.dataset.view === v);
        });
      }
    </script>
    <script src="https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&callback=initMap" async defer></script>
  ` : `
    <script>
      function toggleView(v) {
        document.getElementById('map').style.display = v === 'map' ? 'block' : 'none';
        document.getElementById('streetview').style.display = v === 'streetview' ? 'block' : 'none';
        document.querySelectorAll('.map-toggle-btn').forEach(b => {
          b.classList.toggle('active', b.dataset.view === v);
        });
      }
    </script>
  `;

  const mapSection = MAPS_KEY ? `
    <div id="map" style="width:100%;height:350px;border-radius:12px;"></div>
    <div id="streetview" style="width:100%;height:350px;border-radius:12px;display:none;"></div>
  ` : `
    <iframe id="map" src="https://maps.google.com/maps?q=${lat},${lng}&z=16&output=embed" style="width:100%;height:350px;border:none;border-radius:12px;" allowfullscreen></iframe>
    <iframe id="streetview" src="https://www.google.com/maps/embed?pb=!4v0!6m8!1m7!1s!2m2!1d${lat}!2d${lng}!3f0!4f0!5f0.7820865974627469&layer=streetview&cbll=${lat},${lng}&cbp=12,0,0,0,0" style="width:100%;height:350px;border:none;border-radius:12px;display:none;" allowfullscreen></iframe>
  `;

  // Build AI enrichment section with all fields escaped
  const enrichmentHtml = enrichment ? `
    <div class="ai-section">
      <h3 style="display:flex;align-items:center;gap:6px;">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ac)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L2 7l10 5 10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
        Analise da Localizacao <span style="font-size:10px;color:var(--t3);font-weight:400;margin-left:4px;">por IA</span>
      </h3>

      ${enrichment.locationDescription ? `<p style="font-size:13px;color:var(--t2);margin:8px 0 12px;line-height:1.5">${escapeHtml(enrichment.locationDescription)}</p>` : ''}

      ${enrichment.advantages && enrichment.advantages.length > 0 ? `
        <div style="margin-bottom:10px">
          <div style="font-size:11px;font-weight:600;color:var(--gn);margin-bottom:4px">Vantagens</div>
          ${enrichment.advantages.map(a => `<div style="display:flex;align-items:flex-start;gap:6px;padding:4px 0;font-size:12px;color:var(--t2)"><span style="color:var(--gn);font-weight:700">&#10003;</span> ${escapeHtml(a)}</div>`).join('')}
        </div>
      ` : ''}

      ${enrichment.considerations && enrichment.considerations.length > 0 ? `
        <div style="margin-bottom:10px">
          <div style="font-size:11px;font-weight:600;color:var(--or);margin-bottom:4px">Consideracoes</div>
          ${enrichment.considerations.map(c => `<div style="display:flex;align-items:flex-start;gap:6px;padding:4px 0;font-size:12px;color:var(--t2)"><span style="color:var(--or);font-weight:700">&#9888;</span> ${escapeHtml(c)}</div>`).join('')}
        </div>
      ` : ''}

      ${enrichment.nearbyPOIs && enrichment.nearbyPOIs.length > 0 ? `
        <div style="margin-bottom:10px">
          <div style="font-size:11px;font-weight:600;color:var(--ac);margin-bottom:6px">O que ha por perto</div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:4px">
            ${enrichment.nearbyPOIs.map(poi => `
              <div style="display:flex;align-items:center;gap:6px;padding:6px 8px;background:var(--sf);border-radius:7px;font-size:11px">
                <span style="font-weight:600;color:var(--tx)">${escapeHtml(poi.name || poi.type)}</span>
                ${poi.distance ? `<span style="color:var(--t3);margin-left:auto;font-size:10px">${escapeHtml(poi.distance)}</span>` : ''}
              </div>
            `).join('')}
          </div>
        </div>
      ` : ''}

      ${enrichment.mainStreets && enrichment.mainStreets.length > 0 ? `
        <div style="margin-bottom:6px">
          <div style="font-size:11px;font-weight:600;color:var(--t3);margin-bottom:4px">Vias principais</div>
          <div style="font-size:12px;color:var(--t2)">${enrichment.mainStreets.map(s => escapeHtml(s)).join(' &middot; ')}</div>
        </div>
      ` : ''}

      ${enrichment.centerProximity ? (() => {
        const cp = String(enrichment.centerProximity);
        const isClose = cp === 'proximo' || cp === 'perto';
        const isMid = cp === 'medio' || cp === 'médio';
        const bg = isClose ? 'var(--gl)' : isMid ? 'var(--ol)' : 'var(--rl)';
        const color = isClose ? 'var(--gn)' : isMid ? 'var(--or)' : 'var(--rd)';
        const label = isClose ? 'Proximo' : isMid ? 'Distancia media' : 'Distante';
        return `<div style="display:inline-block;padding:4px 10px;border-radius:6px;font-size:11px;font-weight:600;background:${bg};color:${color}">Centro: ${escapeHtml(label)}</div>`;
      })() : ''}
    </div>
  ` : '';

  const html = `<!DOCTYPE html>
<html${isDark ? ' class="dark"' : ''}>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(p.titulo)} - LupaLeilao</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet">
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'Plus Jakarta Sans',sans-serif; -webkit-font-smoothing:antialiased; background:var(--bg); color:var(--tx); }
    :root {
      --bg:#f7f8fa;--sf:#fff;--s2:#f1f4f8;--s3:#e2e7ee;
      --bd:#dfe4ea;--tx:#0f172a;--t2:#475569;--t3:#94a3b8;
      --br:#ea580c;--bb:rgba(234,88,12,.08);
      --ac:#0284c7;--al:#e0f2fe;
      --gn:#059669;--gl:#d1fae5;
      --rd:#dc2626;--rl:#fee2e2;
      --or:#d97706;--ol:#fef3c7;
      --ct:linear-gradient(135deg,#fff7ed,#ffedd5);
    }
    :root.dark {
      --bg:#0c1017;--sf:#151b27;--s2:#1c2535;--s3:#263044;
      --bd:#1e2d45;--tx:#edf2f7;--t2:#94a3b8;--t3:#64748b;
      --br:#f59e0b;--bb:rgba(245,158,11,.1);
      --ac:#38bdf8;--al:rgba(56,189,248,.12);
      --gn:#34d399;--gl:rgba(52,211,153,.12);
      --rd:#f87171;--rl:rgba(248,113,113,.12);
      --or:#fbbf24;--ol:rgba(251,191,36,.12);
      --ct:linear-gradient(135deg,#1a2744,#1e3050);
    }
    .container { max-width:800px; margin:0 auto; padding:20px; }
    .top-bar { height:4px; background:linear-gradient(90deg,#ea580c,#f59e0b,#fbbf24); }
    .header { margin-bottom:20px; }
    .badges { display:flex; gap:6px; margin-bottom:8px; flex-wrap:wrap; }
    .badge { padding:3px 10px; border-radius:6px; font-size:11px; font-weight:700; display:inline-block; }
    .badge-green { background:var(--gl); color:var(--gn); }
    .badge-orange { background:var(--ol); color:var(--or); }
    .badge-red { background:var(--rl); color:var(--rd); }
    .badge-brand { background:var(--bb); color:var(--br); }
    .badge-bank { color:#fff; }
    h1 { font-size:22px; font-weight:700; line-height:1.3; }
    .address { font-size:13px; color:var(--t3); margin-top:4px; }
    .prices { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:18px; }
    .price-box { border-radius:12px; padding:14px; }
    .price-label { font-size:11px; margin-bottom:2px; }
    .price-old { font-size:17px; font-weight:600; color:var(--t3); text-decoration:line-through; }
    .price-new { font-size:20px; font-weight:800; color:var(--gn); }
    .specs { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; margin-bottom:18px; }
    .spec-box { background:var(--s2); border-radius:10px; padding:10px; text-align:center; }
    .spec-val { font-size:16px; font-weight:700; }
    .spec-lbl { font-size:10px; color:var(--t3); }
    .info-grid { display:grid; grid-template-columns:1fr 1fr; gap:6px; margin-bottom:18px; }
    .info-row { display:flex; justify-content:space-between; padding:8px 12px; background:var(--s2); border-radius:8px; font-size:13px; }
    .info-label { color:var(--t3); }
    .info-value { font-weight:600; }
    .section { margin-bottom:18px; }
    .section h3 { font-size:14px; font-weight:600; color:var(--t2); margin-bottom:8px; }
    .lances-table { border:1px solid var(--bd); border-radius:10px; overflow:hidden; }
    .lance-row { display:flex; justify-content:space-between; padding:8px 14px; font-size:13px; }
    .lance-name { color:var(--t3); }
    .lance-value { font-weight:600; color:var(--gn); }
    .map-section { margin-bottom:18px; }
    .map-toggles { display:flex; gap:6px; margin-bottom:10px; }
    .map-toggle-btn { padding:8px 18px; border-radius:8px; border:1px solid var(--bd); background:var(--sf); color:var(--tx); font-size:13px; font-weight:600; cursor:pointer; font-family:inherit; transition:.2s; }
    .map-toggle-btn.active { background:var(--br); color:#fff; border-color:var(--br); }
    .map-toggle-btn:hover { opacity:.85; }
    .actions { display:flex; flex-direction:column; gap:8px; margin-top:20px; }
    .btn-bank { width:100%; padding:14px; border-radius:12px; border:none; color:#fff; font-weight:700; font-size:15px; cursor:pointer; font-family:inherit; transition:.2s; }
    .btn-bank:hover { opacity:.9; }
    .btn-row { display:flex; gap:8px; }
    .btn-secondary { flex:1; padding:10px; border-radius:10px; border:1px solid var(--bd); background:var(--sf); color:var(--tx); font-weight:600; font-size:13px; cursor:pointer; font-family:inherit; text-align:center; text-decoration:none; display:flex; align-items:center; justify-content:center; }
    .btn-secondary:hover { background:var(--s2); }
    .ai-section { background:var(--s2); border-radius:12px; padding:16px; margin-bottom:18px; }
    .ai-section h3 { font-size:14px; font-weight:600; margin-bottom:6px; }
    .ai-loading { text-align:center; color:var(--t3); font-size:13px; padding:20px 0; }
    .ai-advantages { list-style:none; padding:0; }
    .ai-advantages li { padding:6px 0; font-size:13px; color:var(--t2); border-bottom:1px solid var(--bd); }
    .ai-advantages li:last-child { border-bottom:none; }
    .ai-advantages li::before { content:'\\2713 '; color:var(--gn); font-weight:700; margin-right:6px; }
    .risk-row { display:flex; align-items:center; gap:12px; margin:10px 0; }
    .risk-circle { width:52px; height:52px; border-radius:50%; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
    .risk-inner { width:40px; height:40px; border-radius:50%; background:var(--sf); display:flex; align-items:center; justify-content:center; font-size:16px; font-weight:800; }
    .risk-items { display:flex; flex-direction:column; gap:4px; margin:10px 0; }
    .risk-item { display:flex; align-items:center; gap:8px; padding:7px 12px; background:var(--sf); border-radius:8px; font-size:13px; }
    .risk-item-ok { border-left:3px solid var(--gn); }
    .risk-item-fail { border-left:3px solid var(--rd); }
    .risk-item .icon { font-weight:700; }
    .risk-item .icon-ok { color:var(--gn); }
    .risk-item .icon-fail { color:var(--rd); }
    .risk-item .label { font-weight:600; }
    .risk-item .detail { margin-left:auto; color:var(--t3); font-size:12px; }
    .recommendation { background:var(--ol); border-radius:10px; padding:12px; font-size:13px; color:var(--t2); margin-top:10px; }
    .simulated-label { font-size:10px; color:var(--t3); font-style:italic; margin-top:4px; }

    @media(max-width:640px) {
      .container { padding:14px 12px; }
      h1 { font-size:18px; }
      .prices { grid-template-columns:1fr; }
      .specs { grid-template-columns:repeat(2,1fr); }
      .info-grid { grid-template-columns:1fr; }
      .btn-row { flex-direction:column; }
      .map-toggle-btn { flex:1; text-align:center; }
    }
  </style>
</head>
<body>
  <div class="top-bar"></div>
  <div class="container">
    <!-- Image Gallery -->
    ${galleryHTML}

    <!-- Header -->
    <div class="header">
      <div class="badges">
        <span class="badge badge-${p.status === 'Aberto' || p.status === 'ativo' ? 'green' : p.status === 'Disputa' ? 'orange' : 'red'}">${escapeHtml(p.status)}</span>
        <span class="badge badge-brand">${escapeHtml(p.desconto)}%</span>
        <span class="badge badge-bank" style="background:${bankColor}">${escapeHtml(p.banco)}</span>
      </div>
      <h1>${escapeHtml(p.titulo)}</h1>
      <div class="address">${escapeHtml(p.endereco)}, ${escapeHtml(p.cidade)}/${escapeHtml(p.estado)}</div>
    </div>

    <!-- Prices -->
    <div class="prices">
      <div class="price-box" style="background:var(--s2)">
        <div class="price-label" style="color:var(--t3)">AVALIACAO</div>
        <div class="price-old">${escapeHtml(formatBRL(p.valorAvaliacao))}</div>
      </div>
      <div class="price-box" style="background:var(--gl)">
        <div class="price-label" style="color:var(--gn);font-weight:600">LANCE MINIMO</div>
        <div class="price-new">${escapeHtml(formatBRL(p.valorMinimo))}</div>
      </div>
    </div>

    <!-- Specs -->
    <div class="specs">
      ${specs.map(([val, label]) => `
        <div class="spec-box">
          <div class="spec-val">${val}</div>
          <div class="spec-lbl">${label}</div>
        </div>
      `).join('')}
    </div>

    <!-- Info -->
    <div class="info-grid">
      ${info.map(([label, val]) => `
        <div class="info-row">
          <span class="info-label">${label}</span>
          <span class="info-value">${val}</span>
        </div>
      `).join('')}
    </div>

    <!-- Google Maps -->
    <div class="map-section">
      <div class="section">
        <h3>Localizacao</h3>
        <div class="map-toggles">
          <button class="map-toggle-btn active" data-view="map" onclick="toggleView('map')">Mapa</button>
          <button class="map-toggle-btn" data-view="streetview" onclick="toggleView('streetview')">Street View</button>
        </div>
        ${mapSection}
      </div>
    </div>

    <!-- Property Description -->
    ${p.raw?.descricao ? `
    <div class="section">
      <h3>Descricao do imovel</h3>
      <p style="font-size:13px;color:var(--t2);line-height:1.6;background:var(--s2);border-radius:10px;padding:12px 14px;">${escapeHtml(p.raw.descricao)}</p>
    </div>` : ''}

    <!-- AI Location Enrichment -->
    ${enrichmentHtml}

    <!-- Lances -->
    ${lancesHTML}

    <!-- Actions -->
    <div class="actions">
      <button class="btn-bank" style="background:${bankColor}" id="btn-bank-link">
        Ver no site ${p.banco === 'Caixa' ? 'da Caixa' : 'do Santander'} &#8599;
      </button>
      <div class="btn-row">
        <a class="btn-secondary" href="https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}" target="_blank" rel="noopener noreferrer">
          Rota no Maps &#8599;
        </a>
        <button class="btn-secondary" onclick="window.print()">Imprimir</button>
      </div>
    </div>
  </div>

  <script>
    (function() {
      var bankUrl = ${JSON.stringify(bankUrl)};
      document.getElementById('btn-bank-link').addEventListener('click', function() {
        if (bankUrl && bankUrl !== '#') window.open(bankUrl, '_blank', 'noopener,noreferrer');
      });
    })();
  </script>
  ${galleryScript}
  ${mapsScript}
</body>
</html>`;

  // Open as a real, shareable URL instead of a non-shareable blob: URL.
  // /imovel.html fetches the property from the public API and renders it.
  if (p.id) {
    const themeParam = isDark ? '&theme=dark' : '';
    window.open('/imovel.html?id=' + p.id + themeParam, '_blank', 'noreferrer');
    return;
  }

  // Fallback for cases without an id (e.g. mock/dev data)
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
